/**
 * Impersonation session storage + handoff helpers.
 *
 * The impersonation token lives in tab-scoped `sessionStorage` so it never
 * touches the admin's persistent `localStorage` login. A superAdmin — the only
 * role allowed to impersonate, and never from inside an impersonated tab —
 * starts a session from another user's row; we open a NEW tab and hand the
 * session over via the URL fragment (`#impersonate=...`). The fragment is never
 * sent to the server nor in the `Referer` header, and we strip it on boot — so
 * the token can't be bookmarked, shared, or leaked. Both the Apollo link and the
 * axios interceptor prefer this token over the admin's localStorage token, so
 * the whole tab acts as the target user until the session expires or is ended.
 *
 * The token is minted bound to one app (an `aud` claim equal to the requested
 * `app`), so it only authenticates on the surface it was authorised for — the
 * tab we open must be that same app's origin. See docs/impersonation.md.
 */

export type ImpersonationSession = {
  accessToken: string;
  /** ISO timestamp */
  expiresAt: string;
  /** Public id (usr_…) of the impersonated user, or null when unavailable. */
  targetUserId: string | null;
  /** Public id (usr_…) of the superAdmin actor, or null when unavailable. */
  impersonatorId: string | null;
};

const STORAGE_KEYS = {
  token: "tournated.impersonation.token",
  expiresAt: "tournated.impersonation.expiresAt",
  targetUserId: "tournated.impersonation.targetUserId",
  impersonatorId: "tournated.impersonation.impersonatorId",
} as const;

const URL_HASH_KEY = "impersonate";

const isBrowser = typeof window !== "undefined";

export const clearImpersonationSession = () => {
  if (!isBrowser) return;
  Object.values(STORAGE_KEYS).forEach((key) => window.sessionStorage.removeItem(key));
};

export const writeImpersonationSession = (session: ImpersonationSession) => {
  if (!isBrowser) return;
  window.sessionStorage.setItem(STORAGE_KEYS.token, session.accessToken);
  window.sessionStorage.setItem(STORAGE_KEYS.expiresAt, session.expiresAt);
  // Ids are public-id strings (or null) — store them verbatim, dropping the key
  // when null so a missing id never round-trips back as the literal "null".
  if (session.targetUserId) {
    window.sessionStorage.setItem(STORAGE_KEYS.targetUserId, session.targetUserId);
  } else {
    window.sessionStorage.removeItem(STORAGE_KEYS.targetUserId);
  }
  if (session.impersonatorId) {
    window.sessionStorage.setItem(STORAGE_KEYS.impersonatorId, session.impersonatorId);
  } else {
    window.sessionStorage.removeItem(STORAGE_KEYS.impersonatorId);
  }
};

export const readImpersonationSession = (): ImpersonationSession | null => {
  if (!isBrowser) return null;

  const accessToken = window.sessionStorage.getItem(STORAGE_KEYS.token);
  const expiresAt = window.sessionStorage.getItem(STORAGE_KEYS.expiresAt);
  const targetUserId = window.sessionStorage.getItem(STORAGE_KEYS.targetUserId);
  const impersonatorId = window.sessionStorage.getItem(STORAGE_KEYS.impersonatorId);

  // Only the token + expiry are required to act as a session; the public-id
  // fields are informational and may legitimately be absent (null).
  if (!accessToken || !expiresAt) return null;

  // Auto-clear an expired session so callers never act on a stale token.
  if (new Date(expiresAt).getTime() <= Date.now()) {
    clearImpersonationSession();
    return null;
  }

  return {
    accessToken,
    expiresAt,
    targetUserId,
    impersonatorId,
  };
};

/** Lightweight token accessor for the transport layers (Apollo / axios). */
export const getImpersonationToken = (): string | null =>
  readImpersonationSession()?.accessToken ?? null;

export const isImpersonating = (): boolean => readImpersonationSession() !== null;

/**
 * Builds the URL opened in the new tab. The session rides in the fragment so it
 * never reaches the server or the `Referer` header.
 */
export const buildImpersonationUrl = (
  origin: string,
  path: string,
  session: ImpersonationSession
): string => {
  const normalizedOrigin = origin.replace(/\/+$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const payload = encodeURIComponent(JSON.stringify(session));
  return `${normalizedOrigin}${normalizedPath}#${URL_HASH_KEY}=${payload}`;
};

/**
 * On boot of the impersonated tab: read the session out of the URL fragment,
 * persist it to sessionStorage, and strip the fragment from the URL. Must run
 * before any API request fires so the very first request uses the right token.
 */
export const consumeImpersonationFromUrl = (): ImpersonationSession | null => {
  if (!isBrowser) return null;

  const hash = window.location.hash.replace(/^#/, "");
  const prefix = `${URL_HASH_KEY}=`;
  if (!hash.startsWith(prefix)) return null;

  try {
    const session = JSON.parse(
      decodeURIComponent(hash.slice(prefix.length))
    ) as ImpersonationSession;
    if (!session?.accessToken || !session?.expiresAt) return null;
    writeImpersonationSession(session);
  } catch {
    return null;
  } finally {
    // Always strip the fragment, even on a parse failure, so a malformed token
    // can't linger in the URL.
    window.history.replaceState({}, "", window.location.pathname + window.location.search);
  }

  return readImpersonationSession();
};
